import { flash, Modal, SubmitButton } from "@xola/ui-kit";
import { requiredString } from "common";
import { FormikProps } from "formik";
import { isNull } from "lodash";
import React, { useMemo, useRef, useState } from "react";
import { useMutation } from "react-query";
import { Except } from "type-fest";
import { CollectionNode, CollectionNodeObject, SelectionStateWithSequence } from "types";
import { object, string } from "yup";
import { SecondaryButton } from "../../../Common/SecondaryButton";
import { XolaFormik } from "../../../Common/XolaFormik";
import { BulkUpdateErrorModal } from "./BulkUpdateErrorModal";
import { EditCollectionFields } from "./EditCollectionFields";
import { extractProductCollectionNode, getPatchPayload, getPostPayload } from "./EditCollectionHelper";
import { api } from "@/api/api";
import { uploadFile } from "@/api/mediaApi";
import { xolaUri } from "@/api/xolaUri";
import { config } from "@/config";
import { useSeller } from "@/hooks/useAccount";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useModal } from "@/hooks/useVisibility";
import { getConstructedItemPath } from "@/models/CollectionNode";

interface EditCollectionModalProps {
    collection?: CollectionNode;
    isOpen: boolean;
    onClose: () => void;
    afterSave?: (collection: CollectionNode) => Promise<void>;
}

interface AddCollectionFormState {
    path: string;
    title: string;
    description: string;
    items: SelectionStateWithSequence;
}

interface CreateCollectionPayload {
    path?: string;
    seller?: { id: ID };
    name?: string;
    description?: string;
    object?: CollectionNodeObject;
    product?: { id: ID };
    sequence?: number;
    children?: Array<{
        id?: ID;
        object: CollectionNodeObject;
        product?: { id: ID };
        sequence?: number;
        _remove?: boolean;
    }>;
}

export const EditCollectionModal = ({ collection, isOpen, onClose, afterSave }: EditCollectionModalProps) => {
    return (
        <Modal size="large" isOpen={isOpen} onClose={onClose}>
            <ModalContent collection={collection} afterSave={afterSave} onClose={onClose} />
        </Modal>
    );
};

const validationSchema = object().shape({
    parentCollection: string(),
    title: requiredString(),
    description: string(),
    items: object(),
});

const ModalContent = ({ collection, onClose, afterSave }: Except<EditCollectionModalProps, "isOpen">) => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const [picture, setPicture] = useState<File | undefined | null>();
    const [failedProductIds, setFailedProductIds] = useState<string[]>([]);
    const errorModal = useModal();
    const formikRef = useRef<FormikProps<AddCollectionFormState>>(null);

    // Fetch children if editing existing collection
    const collectionPath = collection?.id ? getConstructedItemPath(collection.path ?? "", collection.id) : undefined;
    const {
        data: productCollectionItem = [],
        isLoading: isLoadingCollectionNode,
        isFetchingNextPage: isFetchingNextPageCollectionNode,
    } = useAutoFetchCollection<CollectionNode>(
        "collectionNodes",
        { path: collectionPath },
        { enabled: !!collection?.id },
    );

    // Extract collection items - we'll use this for initial values and updates
    const { items: currentItems, itemsMap: existingItemsMap } = useMemo(
        () => extractProductCollectionNode(productCollectionItem),
        [productCollectionItem],
    );

    // Merge current form values with new items to preserve user input
    const initialValues = useMemo(() => {
        const currentValues = formikRef.current?.values;
        return getInitialValues(collection, currentItems, currentValues);
    }, [collection, currentItems]);

    const { mutateAsync: upsertCollection, isLoading } = useMutation(async (payload: CreateCollectionPayload) => {
        if (collection?.id) {
            return api.patch<CollectionNode>(`/collectionNodes/${collection.id}`, payload);
        }
        return api.post<CollectionNode>("/collectionNodes", payload);
    });

    const uploadPicture = async (collectionItem?: CollectionNode) => {
        if (picture) {
            const url = `${config.xola.url}/api/collectionNodes/${collectionItem?.id}/photo?seller=${seller.id}`;
            await uploadFile(url, picture, { "X-API-VERSION": config.xola.apiVersion });
        }
    };

    const handleSubmit = async (values: AddCollectionFormState) => {
        const payload: CreateCollectionPayload = collection?.id
            ? getPatchPayload(values, collection, existingItemsMap)
            : getPostPayload(values, seller.id);

        try {
            if (collection?.id) {
                // PATCH logic for editing existing collection
                // Only send PATCH if there are changes
                if (Object.keys(payload).length > 0 || picture !== undefined) {
                    const { data } = await upsertCollection(payload);

                    // Handle image upload and delete old image if new one is uploaded
                    if (picture) {
                        // Upload new image
                        await uploadPicture(data);
                    }

                    if (afterSave) {
                        await afterSave(data);
                    }

                    invalidate("collectionNodes");
                    flash.success("Collection updated successfully");
                }
            } else {
                // POST logic for creating new collection
                const { data } = await upsertCollection(payload);

                // Handle image upload
                if (picture) {
                    await uploadPicture(data);
                }

                if (afterSave) {
                    await afterSave(data);
                }

                invalidate("collectionNodes");
                flash.success("Collection created successfully");
            }

            onClose();
        } catch (error: any) {
            const fieldData = error?.response?.data?.field;
            const isBulkOperationFailed =
                fieldData?.children?.reason === "bulk_operations_failed" ||
                fieldData?.ops?.reason === "bulk_operations_failed";

            if (isBulkOperationFailed) {
                const errors = fieldData?.children?.errors || fieldData?.ops?.errors || [];

                // Extract product IDs from failed items
                const productIds: string[] = [];
                for (const err of errors) {
                    const itemIndex = err.index;
                    const failedChild = payload.children?.[itemIndex];
                    if (failedChild?.product?.id) {
                        productIds.push(failedChild.product.id);
                    } else if (err.document?.product?.id) {
                        productIds.push(err.document.product.id);
                    }
                }

                setFailedProductIds(productIds);
                errorModal.show();
            } else {
                flash.error("Failed to save collection. Please try again.");
            }
        }
    };

    const getImageUrl = () => {
        if (isNull(picture)) {
            // image has been deleted
            return "";
        }

        if (picture) {
            return URL.createObjectURL(picture);
        }

        if (collection?.photo?.src) {
            return `${xolaUri}${collection?.photo.src}`;
        }

        return "";
    };

    const handleImageDelete = () => {
        setPicture(null);
    };

    const isImageChanged = collection?.photo?.src
        ? `${xolaUri}${collection?.photo.src}` !== getImageUrl()
        : !!getImageUrl();

    return (
        <>
            <XolaFormik
                enableReinitialize
                innerRef={formikRef}
                initialValues={initialValues}
                validationSchema={validationSchema}
                validateOnChange={false}
                validateOnBlur={false}
                onSubmit={handleSubmit}
            >
                {({ handleSubmit, dirty, isSubmitting, values }) => {
                    const hasTitleChange = values.title !== (collection?.name ?? "");
                    const hasDescriptionChange = values.description !== (collection?.description ?? "");
                    const isDirty = dirty || hasTitleChange || hasDescriptionChange;

                    return (
                        <>
                            <Modal.Header>{collection ? "Edit" : "Add"} Collection</Modal.Header>
                            <Modal.Body>
                                <EditCollectionFields
                                    imageUrl={getImageUrl()}
                                    isLoadingItems={isLoadingCollectionNode}
                                    isFetchingNextPageCollectionNode={isFetchingNextPageCollectionNode}
                                    currentCollectionId={collection?.id}
                                    onImageChange={(file: File) => setPicture(file)}
                                    onImageDelete={handleImageDelete}
                                />
                            </Modal.Body>
                            <Modal.Footer>
                                <SecondaryButton disabled={isLoading || isSubmitting} onClick={onClose}>
                                    Cancel
                                </SecondaryButton>

                                <SubmitButton
                                    isLoading={isLoading || isSubmitting}
                                    disabled={isLoadingCollectionNode || !(isDirty || isImageChanged)}
                                    onClick={handleSubmit}
                                >
                                    Save
                                </SubmitButton>
                            </Modal.Footer>
                        </>
                    );
                }}
            </XolaFormik>
            <BulkUpdateErrorModal
                isOpen={errorModal.isVisible}
                productIds={failedProductIds}
                onClose={errorModal.hide}
            />
        </>
    );
};

const getInitialValues = (
    collection: CollectionNode | undefined,
    items: SelectionStateWithSequence["selected"],
    currentValues?: AddCollectionFormState,
): AddCollectionFormState => {
    return {
        path: currentValues?.path ?? collection?.path ?? "",
        title: currentValues?.title ?? collection?.name ?? "",
        description: currentValues?.description ?? collection?.description ?? "",
        items: {
            all: false,
            selected: items,
        },
    };
};
